import { Link } from "@inertiajs/react"
import { Lock, ArrowRight } from "lucide-react"

import { formatDate } from "@/components/tuw/insider/format"

// A small state pill.
function Pill({ tone, children }) {
  const tones = {
    green: "bg-[color-mix(in_oklab,var(--done)_14%,transparent)] text-done",
    amber: "bg-[color-mix(in_oklab,var(--hold)_16%,transparent)] text-hold",
    red: "bg-[var(--red-soft)] text-primary",
    muted: "bg-secondary text-ink-3",
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold ${tones[tone]}`}>
      {children}
    </span>
  )
}

function CardFrame({ children }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="border-b border-border px-[22px] py-4">
        <h3 className="font-display text-[14.5px] font-bold">Insider membership</h3>
      </div>
      <div className="flex flex-col gap-3 px-[22px] py-[18px]">{children}</div>
    </div>
  )
}

// The Insider membership card. Its state is derived server-side by reusing
// CustomerInsiderPresenter (D5), so this component only composes per state and
// reuses the Insider hub's date formatter (DRY). Active members see their dates
// + a hub link; lapsed/never-joined customers see a slim upsell.
export default function InsiderCard({ insider }) {
  const state = insider?.state

  if (state === "member" || state === "welcome") {
    return (
      <CardFrame>
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone="green">Active</Pill>
          <Pill tone="muted">
            <Lock className="size-3" strokeWidth={2.4} />
            Locked rate
          </Pill>
        </div>
        <p className="text-[13px] leading-snug text-ink-3">
          {insider?.activeSince && <>Member since {formatDate(insider.activeSince)}. </>}
          {insider?.nextBilling && <>Next billing {formatDate(insider.nextBilling)}.</>}
        </p>
        <Link href={insider.hubPath} className="inline-flex items-center gap-1 text-[13px] font-semibold text-primary no-underline hover:underline">
          Manage in Insider Hub
          <ArrowRight className="size-3.5" strokeWidth={2.2} />
        </Link>
      </CardFrame>
    )
  }

  if (state === "past_due") {
    return (
      <CardFrame>
        <Pill tone="amber">Payment due</Pill>
        <p className="text-[13px] leading-snug text-ink-3">
          Your Insider payment didn&apos;t go through. Update it to keep your locked rate.
        </p>
        <Link href={insider.hubPath} className="inline-flex items-center gap-1 text-[13px] font-semibold text-primary no-underline hover:underline">
          Fix payment in Insider Hub
          <ArrowRight className="size-3.5" strokeWidth={2.2} />
        </Link>
      </CardFrame>
    )
  }

  if (state === "canceled") {
    return (
      <CardFrame>
        <Pill tone="muted">Membership ended</Pill>
        <p className="text-[13px] leading-snug text-ink-3">
          Your Insider membership has ended. Rejoin to lock in member pricing again.
        </p>
        <Link href={insider.hubPath} className="inline-flex items-center gap-1 text-[13px] font-semibold text-primary no-underline hover:underline">
          Rejoin Insider
          <ArrowRight className="size-3.5" strokeWidth={2.2} />
        </Link>
      </CardFrame>
    )
  }

  // offer / closed / unavailable — the upsell.
  return (
    <CardFrame>
      <p className="text-[13px] leading-snug text-ink-3">
        Insider locks in member pricing on every project and unlocks member perks.
      </p>
      <Link href="/insider" className="inline-flex items-center gap-1 text-[13px] font-semibold text-primary no-underline hover:underline">
        Lock in Insider pricing
        <ArrowRight className="size-3.5" strokeWidth={2.2} />
      </Link>
    </CardFrame>
  )
}
